import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Btn, Card, Empty, Loading, Pill, Textarea } from "@/components/Ui";

const when = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { day: "2-digit", month: "short" });
};

export default function Messages({ role }) {
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null);
  const [convo, setConvo] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const loadThreads = useCallback(() => {
    api.get("/messages/threads").then((r) => setThreads(r.data)).catch(() => setThreads([]));
  }, []);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  const openThread = useCallback((id) => {
    setActive(id);
    setConvo(null);
    api.get(`/messages/threads/${id}`)
      .then((r) => { setConvo(r.data); loadThreads(); })
      .catch((e) => { toast.error(errMsg(e)); setConvo(false); });
  }, [loadThreads]);

  useEffect(() => {
    if (convo?.messages?.length) endRef.current?.scrollIntoView({ block: "end" });
  }, [convo]);

  const send = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/messages/threads/${active}/messages`, { body: draft });
      setConvo((c) => ({ ...c, messages: [...(c?.messages || []), data] }));
      setDraft("");
      loadThreads();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  if (!threads) return <Loading label="Loading conversations…" />;

  if (!threads.length)
    return (
      <Empty testId="messages-empty" title="No conversations yet"
        sub={role === "buyer"
          ? "Open a conversation from a quotation to ask a supplier about their offer."
          : "Buyers can start a conversation with you about an enquiry you were invited to."} />
    );

  return (
    <div className="grid gap-5 lg:grid-cols-[300px_1fr]" data-testid="messages-page">
      <div className="space-y-2" data-testid="thread-list">
        {threads.map((t) => (
          <button key={t.id} data-testid={`thread-${t.id}`} onClick={() => openThread(t.id)}
            className={`w-full rounded-[12px] border p-3.5 text-left transition-colors ${
              active === t.id
                ? "border-[#102A43] bg-[#f5f7fa]"
                : "border-[#DFE6ED] bg-white hover:border-[#9FB3C8]"
            }`}>
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-bold text-[#102A43]">{t.counterparty || "—"}</p>
              {t.unread > 0 && (
                <Pill tone="gold" testId={`thread-unread-${t.id}`}>{t.unread}</Pill>
              )}
            </div>
            <p className="mt-0.5 text-xs font-semibold text-[#147D78]">{t.rfq_number}</p>
            <p className="mt-1.5 line-clamp-2 text-xs text-[#667788]">
              {t.last_message || "No messages yet"}
            </p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.1em] text-[#8598ac]">
              {when(t.last_message_at)}
            </p>
          </button>
        ))}
      </div>

      <div className="min-w-0">
        {!active && (
          <Card>
            <div className="flex items-center gap-3 text-[#667788]">
              <MessageSquare className="h-5 w-5 text-[#147D78]" />
              <p className="text-sm">Select a conversation to read and reply.</p>
            </div>
          </Card>
        )}
        {active && !convo && <Loading label="Opening conversation…" />}
        {active && convo && (
          <div className="flex h-[560px] flex-col rounded-[14px] border border-[#DFE6ED] bg-white"
            data-testid="conversation">
            <div className="border-b border-[#DFE6ED] px-4 py-3">
              <p className="text-sm font-bold text-[#102A43]" data-testid="conversation-title">
                {convo.thread.counterparty}
              </p>
              <p className="text-xs text-[#667788]">
                {convo.thread.rfq_number} · {convo.thread.rfq_title}
              </p>
            </div>

            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="message-list">
              {convo.messages.length === 0 && (
                <p className="text-sm text-[#667788]">
                  No messages yet — send the first one below.
                </p>
              )}
              {convo.messages.map((m) => (
                <div key={m.id} data-testid={`message-${m.id}`}
                  className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[78%] overflow-hidden rounded-[12px] px-3.5 py-2.5 ${
                    m.mine ? "bg-[#102A43] text-white" : "border border-[#DFE6ED] bg-[#f8fafc] text-[#18232F]"
                  }`}>
                    <p className={`text-[10px] font-semibold uppercase tracking-[0.1em] ${
                      m.mine ? "text-white/60" : "text-[#8598ac]"}`}>
                      {m.sender_name} · {when(m.created_at)}
                    </p>
                    <p className="mt-1 whitespace-pre-wrap break-words text-sm">{m.body}</p>
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            <form onSubmit={send} className="border-t border-[#DFE6ED] p-3">
              <Textarea data-testid="message-input" rows={2} value={draft}
                placeholder="Write a message about this enquiry…"
                onChange={(e) => setDraft(e.target.value)} />
              <div className="mt-2 flex justify-end">
                <Btn type="submit" data-testid="send-message-btn" loading={busy}
                  disabled={!draft.trim()} className="px-5 py-2.5 text-xs">
                  <Send className="h-3.5 w-3.5" /> Send
                </Btn>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
